/**
 * The per-account IndexedDB database behind the offline layer.
 *
 * Opened lazily on first use, always for an account the caller names: "which
 * account" is resolved once per request and threaded through, so a sign-out
 * and a different sign-in midway through a request cannot make its read or
 * its write land in another account's store. Every write also carries the
 * generation captured when its request started; a write from a previous
 * generation is dropped, which is what makes a wipe safe against responses
 * still in flight.
 *
 * The cache is an optimisation and never a dependency: a store that will not
 * open, read or write is reported as a miss, never as a failed request.
 */

import { deleteDB, openDB } from 'idb';

import { logger } from '@/utils/logger';

import {
  currentAccountId,
  currentStoreGeneration,
  databaseNameFor,
  rememberAccount,
} from './account';

import type { DBSchema, IDBPDatabase, IDBPTransaction, StoreNames } from 'idb';

export const SCHEMA_VERSION = 1;

/** One cached response. `key` is "METHOD path?sorted-query". */
export interface ResourceRow<T = unknown> {
  key: string;
  data: T;
  fetchedAt: number;
}

/** Where a queued write stands: waiting, being sent, or given up on. */
export type OutboxState = 'queued' | 'inflight' | 'failed';

/** The HTTP verbs the outbox can replay. */
export type WriteMethod = 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/**
 * One queued write. `seq` orders rows within an entity; `state` follows the
 * spec's table; the expected revision is resolved at flush time, not enqueue.
 */
export interface OutboxRow {
  id: string;
  seq: number;
  entityKey: string;
  method: WriteMethod;
  path: string;
  body: unknown;
  state: OutboxState;
  attempts: number;
  createdAt: number;
  lastError?: string;
  lastStatus?: number;
  /** Earliest time a 5xx-backed-off row may be retried. */
  nextAttemptAt?: number;
  /** The outbox id of a queued or failed create this row depends on, if any. */
  dependsOnCreate?: string;
}

export interface OfflineSchema extends DBSchema {
  resources: { key: string; value: ResourceRow };
  outbox: { key: string; value: OutboxRow; indexes: { byEntity: string } };
  meta: { key: string; value: { key: string; value: unknown } };
}

/** The three object stores every offline transaction spans. */
export type OfflineStores = 'resources' | 'outbox' | 'meta';

/**
 * The transaction modes the offline layer opens.
 *
 * Spelled out rather than aliasing the DOM's `IDBTransactionMode`: that name
 * exists only in the type system, so ESLint's `no-undef` — which looks for a
 * runtime binding — reports every use of it.
 */
export type OfflineTxMode = 'readonly' | 'readwrite' | 'versionchange';

/** A transaction over all three stores, in a caller-chosen mode. */
export type OfflineTx<M extends OfflineTxMode> = IDBPTransaction<OfflineSchema, OfflineStores[], M>;

/** Who a write belongs to and which store generation it started in; fixed for its whole life. */
export interface WriteScope {
  account: string;
  generation: number;
}

/** The scope for a write starting now, or null when nobody is signed in. */
export function captureScope(): WriteScope | null {
  const account = currentAccountId();
  return account ? { account, generation: currentStoreGeneration(account) } : null;
}

/** True while the scope still describes the live session and store. */
export function scopeIsCurrent(scope: WriteScope): boolean {
  return (
    currentAccountId() === scope.account &&
    currentStoreGeneration(scope.account) === scope.generation
  );
}

let openPromise: Promise<IDBPDatabase<OfflineSchema>> | null = null;
let openedFor: string | null = null;

/**
 * The one `openDB` call, so every connection to an account's database runs
 * the same upgrade: a connection opened without it would create an empty
 * database at the current version, and the real open would then never build
 * the stores.
 */
function openAccountDb(
  account: string,
  blocking: () => void
): Promise<IDBPDatabase<OfflineSchema>> {
  return openDB<OfflineSchema>(databaseNameFor(account), SCHEMA_VERSION, {
    upgrade(db, oldVersion, newVersion, tx) {
      exportOutboxOnUpgrade(db, oldVersion, newVersion, tx);
      if (!db.objectStoreNames.contains('resources')) {
        db.createObjectStore('resources', { keyPath: 'key' });
      }
      if (!db.objectStoreNames.contains('outbox')) {
        const outbox = db.createObjectStore('outbox', { keyPath: 'id' });
        outbox.createIndex('byEntity', 'entityKey');
      }
      if (!db.objectStoreNames.contains('meta')) {
        db.createObjectStore('meta', { keyPath: 'key' });
      }
    },
    blocking,
  });
}

/**
 * Open (or reuse) one account's database; null unless it is still signed in.
 *
 * Deliberately not `async`: the memo has to be published before the function
 * yields, or two callers in the same tick each open a connection and the
 * second one is never tracked — and a connection nobody can close blocks
 * `deleteDatabase` forever, which hangs every wipe. Closing the previous
 * connection therefore happens *inside* the promise being memoised.
 */
function getDb(account: string): Promise<IDBPDatabase<OfflineSchema> | null> {
  if (currentAccountId() !== account) return Promise.resolve(null);
  if (openPromise && openedFor === account) return openPromise;
  const previous = openPromise;
  openedFor = account;
  const pending = (async () => {
    if (previous) {
      try {
        (await previous).close();
      } catch {
        // The previous open failed; there is no connection to close.
      }
    }
    rememberAccount(account);
    // Another tab is deleting or upgrading: let go of this connection so it
    // can proceed, leaving any newer one alone.
    return openAccountDb(account, () => {
      void pending.then(db => db.close()).catch(() => undefined);
      if (openPromise !== pending) return;
      openPromise = null;
      openedFor = null;
    });
  })();
  openPromise = pending;
  // A failed open must not stay memoised, or one bad moment disables the
  // cache for the life of the tab; drop it so the next call opens again.
  pending.catch(() => {
    if (openPromise !== pending) return;
    openPromise = null;
    openedFor = null;
  });
  return pending;
}

/**
 * Run `fn` inside one transaction over all three stores, for a scope.
 *
 * Returns null, without running `fn`, when the scope is no longer current
 * (signed out, a different account, or a wipe raced this write). A throw
 * inside `fn` aborts the transaction and propagates, so an enqueue can never
 * half-commit. The scope is re-checked after the database opens, because
 * opening can yield.
 *
 * `fn` must await only promises from the transaction's stores; awaiting
 * anything else lets IndexedDB auto-commit, after which the abort below is a
 * no-op and a later throw leaves the earlier writes committed.
 */
export async function withStores<T, M extends OfflineTxMode>(
  scope: WriteScope,
  mode: M,
  fn: (tx: OfflineTx<M>) => Promise<T>
): Promise<T | null> {
  if (!scopeIsCurrent(scope)) return null;
  const db = await getDb(scope.account);
  if (!db || !scopeIsCurrent(scope)) return null;
  const tx = db.transaction(['resources', 'outbox', 'meta'], mode) as OfflineTx<M>;
  try {
    const result = await fn(tx);
    await tx.done;
    return result;
  } catch (err) {
    // The abort below rejects `tx.done`; claim that rejection here or it
    // escapes as an unhandled one, long after the caller saw the real error.
    tx.done.catch(() => undefined);
    try {
      tx.abort();
    } catch (abortErr) {
      // The transaction had already finished, so there was nothing left to
      // undo — which means whatever `fn` wrote before it threw is committed.
      logger.warn(
        'offline: could not abort after a failed store write; it may have partly committed',
        abortErr
      );
    }
    throw err;
  }
}

/** Every outbox row for an account, oldest `seq` first; empty when the store is unavailable. */
export async function listOutbox(account: string): Promise<OutboxRow[]> {
  try {
    const db = await getDb(account);
    if (!db) return [];
    return (await db.getAll('outbox')).sort((a, b) => a.seq - b.seq);
  } catch (err) {
    logger.debug('offline: outbox read failed, treating it as empty', err);
    return [];
  }
}

/**
 * Whether an account's database exists, without creating it. Where the
 * browser cannot list databases the answer is "assume so": opening one that
 * is missing then creates a well-formed empty store, which is harmless.
 */
async function databaseExists(account: string): Promise<boolean> {
  const list = indexedDB.databases?.bind(indexedDB);
  if (!list) return true;
  const name = databaseNameFor(account);
  return (await list()).some(db => db.name === name);
}

/**
 * Every outbox row for an account that is not signed in — the login wall,
 * asking what the last session left behind. `listOutbox` reads nothing for an
 * account without a session, by design; this opens a short-lived connection
 * of its own and closes it before returning, so it never blocks a wipe.
 * Empty when the database does not exist or on any failure.
 */
export async function listOutboxWithoutSession(account: string): Promise<OutboxRow[]> {
  let db: IDBPDatabase<OfflineSchema> | null = null;
  try {
    if (!(await databaseExists(account))) return [];
    const opening = openAccountDb(account, () => {
      void opening.then(opened => opened.close()).catch(() => undefined);
    });
    db = await opening;
    return (await db.getAll('outbox')).sort((a, b) => a.seq - b.seq);
  } catch (err) {
    logger.debug('offline: signed-out outbox read failed, treating it as empty', err);
    return [];
  } finally {
    db?.close();
  }
}

/**
 * Upgrade hook for a future schema bump: move queued writes into
 * `meta.orphanedOutbox` (appending to anything already there) and empty the
 * outbox, so a bump can never silently drop an entry written offline and a
 * row from an old schema is never replayed against a new one. Wired into
 * `openDB` now (a no-op at version 1).
 *
 * The copy and the clear are two steps, so a failure between them is exactly
 * the half-migrated state this function exists to prevent: rows duplicated
 * into `orphanedOutbox` and still queued for replay against a schema they
 * were not written for. A failure therefore aborts the versionchange
 * transaction, which rolls the whole upgrade back and rejects the `openDB`
 * that started it, leaving the database at its old version with its rows
 * intact.
 */
export function exportOutboxOnUpgrade(
  db: IDBPDatabase<OfflineSchema>,
  oldVersion: number,
  _newVersion: number | null,
  tx: IDBPTransaction<OfflineSchema, ArrayLike<StoreNames<OfflineSchema>>, 'versionchange'>
): void {
  if (oldVersion < 1 || !db.objectStoreNames.contains('outbox')) return;
  const outbox = tx.objectStore('outbox');
  const meta = tx.objectStore('meta');
  void (async () => {
    const rows = await outbox.getAll();
    if (rows.length === 0) return;
    const earlier = ((await meta.get('orphanedOutbox'))?.value as OutboxRow[] | undefined) ?? [];
    await meta.put({ key: 'orphanedOutbox', value: [...earlier, ...rows] });
    await outbox.clear();
    // Threaded revisions belong to the rows just orphaned; a later write must not inherit them.
    for (const key of await meta.getAllKeys()) {
      if (String(key).startsWith('rev:')) await meta.delete(key);
    }
  })().catch(err => {
    logger.debug('offline: outbox export on upgrade failed; aborting the upgrade', err);
    // The abort rejects `tx.done`, which nothing else here awaits; claim that
    // rejection or it escapes as an unhandled one after `openDB` has already
    // reported the failure.
    tx.done.catch(() => undefined);
    try {
      tx.abort();
    } catch {
      // The transaction already finished; there is nothing left to roll back.
    }
  });
}

/** Read one cached resource; null on a miss, a changed account, or any failure. */
export async function getResource<T>(key: string, account: string): Promise<ResourceRow<T> | null> {
  try {
    const db = await getDb(account);
    if (!db) return null;
    const row = await db.get('resources', key);
    return (row as ResourceRow<T> | undefined) ?? null;
  } catch (err) {
    logger.debug('offline: cache read failed, treating it as a miss', err);
    return null;
  }
}

/**
 * Write one cached resource, unless the generation moved since the request
 * started (a wipe happened underneath it) or the account has changed.
 *
 * A store that refuses the write — out of quota, corrupt, mid-delete — is not
 * the caller's problem, so the failure is logged and swallowed.
 */
export async function putResource<T>(
  key: string,
  data: T,
  account: string,
  generation: number
): Promise<void> {
  if (currentStoreGeneration(account) !== generation) return;
  try {
    const db = await getDb(account);
    if (!db) return;
    await db.put('resources', { key, data, fetchedAt: Date.now() });
  } catch (err) {
    logger.debug('offline: cache write failed, leaving the cache as it was', err);
  }
}

/**
 * Delete one cached resource, for an account the caller names.
 *
 * Used to invalidate a key whose server-side copy the app has just changed. A
 * store that will not open or will not delete leaves the row where it is: the
 * next read serves it, which is a stale read, never a failed one.
 */
export async function deleteResource(key: string, account: string): Promise<void> {
  try {
    const db = await getDb(account);
    if (!db) return;
    await db.delete('resources', key);
  } catch (err) {
    logger.debug('offline: cache delete failed, leaving the row as it was', err);
  }
}

/** Read one per-account setting; null on a miss, a changed account, or any failure. */
export async function getMeta<T>(key: string, account: string): Promise<T | null> {
  try {
    const db = await getDb(account);
    if (!db) return null;
    const row = await db.get('meta', key);
    return (row?.value as T | undefined) ?? null;
  } catch (err) {
    logger.debug('offline: meta read failed, treating it as a miss', err);
    return null;
  }
}

/**
 * Write one per-account setting, reporting whether it actually landed.
 *
 * A refused write is not the caller's problem to handle, but it is theirs to
 * know about: a setting that records "this was shown once" must not be
 * believed by a caller whose store never kept it.
 */
export async function setMeta<T>(key: string, value: T, account: string): Promise<boolean> {
  try {
    const db = await getDb(account);
    if (!db) return false;
    await db.put('meta', { key, value });
    return true;
  } catch (err) {
    logger.debug('offline: meta write failed, leaving it unset', err);
    return false;
  }
}

/**
 * Close the connection so a delete in this or another tab is not blocked.
 *
 * Resolves once the connection is actually closed, so a caller that is about
 * to delete the database can await it; `void closeStore()` stays correct for
 * callers that only need to let go.
 */
export async function closeStore(): Promise<void> {
  const pending = openPromise;
  openPromise = null;
  openedFor = null;
  if (!pending) return;
  try {
    (await pending).close();
  } catch {
    // The open failed, so there is no connection to close.
  }
}

/** Delete an account's database, releasing our own connection first. */
export async function deleteStore(accountId: string): Promise<void> {
  await closeStore();
  await deleteDB(databaseNameFor(accountId), {
    blocked() {
      logger.debug('offline: delete blocked by another connection to', accountId);
    },
  });
}
